import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "react-router-dom";
import WhatsAppButton from "@/components/WhatsAppButton";
import { breadcrumbSchema, faqSchema, articleSchema } from "@/lib/schema";

/**
 * Reusable pillar-page template for high-intent AEO / GEO content.
 * Each pillar is a long-form guide that also emits Article + FAQ schema
 * so it wins featured snippets and AI-Overview citations.
 */
export default function PillarPage({
  slug,
  category,
  title,
  hero,
  intro,
  updated,
  toc,        // [{ id, label }]
  sections,   // [{ id, heading, body: JSX }]
  faqs,       // [{ q, a }]
  cta = { label: "Speak to an advisor", to: "/contact" },
}) {
  const url = `/${slug}`;
  const post = {
    slug,
    title,
    summary: intro,
    hero_image: hero,
    published_at: "2026-01-01T00:00:00Z",
    updated_at: updated || "2026-02-01T00:00:00Z",
    category,
    tags: [category, "Dubai", "UAE property"],
  };
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Journal", path: "/journal" },
    { name: title, path: url },
  ];

  return (
    <div data-testid={`pillar-${slug}`}>
      <SEO
        title={title}
        description={intro?.slice(0, 160)}
        path={url}
        image={hero}
        type="article"
        schema={[breadcrumbSchema(crumbs), faqSchema(faqs), articleSchema(post)]}
      />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden bg-[#001588]">
        <img src={hero} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-80" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent pointer-events-none"></div>
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 pb-16 w-full text-white" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}>
          <Breadcrumbs items={crumbs} />
          <div className="overline text-white/80 mb-5 mt-4">{category} · Updated {updated || "Feb 2026"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">{title}</h1>
        </div>
      </section>

      {/* Body — 2-col with sticky TOC on desktop for premium editorial feel */}
      <section className="py-16 lg:py-24 border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28">
              <div className="overline text-[#001588] mb-4">Contents</div>
              <ol className="space-y-3 text-sm text-neutral-700">
                {toc.map((t, i) => (
                  <li key={t.id} className="flex gap-3">
                    <span className="text-[#001588] tabular-nums w-6">{String(i + 1).padStart(2, "0")}</span>
                    <a href={`#${t.id}`} className="hover:text-[#001588] transition-colors">{t.label}</a>
                  </li>
                ))}
              </ol>
              <div className="mt-8">
                <WhatsAppButton className="w-full" message={`Hi Point Penta, I read your ${title} guide and would like personal advice.`} testId={`pillar-whatsapp-${slug}`} />
              </div>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 order-1 lg:order-2">
            <p className="text-xl leading-relaxed text-neutral-800 mb-14 font-light">{intro}</p>
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="mb-14 scroll-mt-28">
                <h2 className="font-serif text-3xl sm:text-4xl mb-6 text-[#050d19]">{s.heading}</h2>
                <div className="prose prose-neutral max-w-none text-neutral-800 leading-relaxed space-y-4 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_table]:w-full [&_table]:text-sm [&_table]:border-collapse [&_th]:bg-neutral-50 [&_th]:border [&_th]:border-neutral-200 [&_th]:p-3 [&_th]:text-left [&_th]:uppercase [&_th]:text-xs [&_th]:tracking-wider [&_th]:text-neutral-600 [&_td]:border [&_td]:border-neutral-200 [&_td]:p-3 [&_a]:text-[#001588] [&_a]:underline">
                  {s.body}
                </div>
              </section>
            ))}
          </article>
        </div>
      </section>

      {/* FAQ — visible + schema-backed */}
      <section className="py-16 lg:py-24 bg-[#F5F7F6]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-4">Frequently asked</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10">{title.split(" — ")[0]} — quick answers</h2>
          <div className="divide-y divide-neutral-300 border-t border-neutral-300">
            {faqs.map((f, i) => (
              <details key={i} className="py-5 group" data-testid={`pillar-faq-${slug}-${i}`}>
                <summary className="cursor-pointer flex justify-between items-start gap-4 list-none">
                  <h3 className="text-base sm:text-lg font-medium text-[#050d19] group-open:text-[#001588]">{f.q}</h3>
                  <span className="text-[#001588] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-[#001588] mb-4">Ready to go deeper?</div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-[#050d19]">
              A Point Penta advisor can walk you through the exact numbers for your case in one call.
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to={cta.to} className="px-6 py-4 bg-[#001588] text-white text-center tracking-wide hover:bg-black transition-colors" data-testid={`pillar-cta-${slug}`}>
              {cta.label}
            </Link>
            <Link to="/calculators" className="px-6 py-4 border border-[#001588] text-[#001588] text-center tracking-wide hover:bg-[#001588] hover:text-white transition-colors">
              Run the calculators
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
